import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Mail, Phone, Instagram, Facebook, MessageCircle, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SectionHeading from '../components/SectionHeading';
import LaserBorder from '../components/LaserBorder';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Get in Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Contact <span className="text-gradient">Us</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Have questions, suggestions, or want to get involved? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 lg:py-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="text-center p-12 rounded-2xl bg-card border border-border" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-semibold font-['Outfit'] text-foreground mb-4">Message Sent!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <Button 
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', subject: '', message: '' });
                    }}
                    variant="outline"
                    className="rounded-full"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="h-12 rounded-xl"
                        data-testid="contact-name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="h-12 rounded-xl"
                        data-testid="contact-email"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="How can we help?"
                      className="h-12 rounded-xl"
                      data-testid="contact-subject"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Your message..."
                      className="min-h-[150px] rounded-xl"
                      data-testid="contact-message"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-semibold"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <LaserBorder>
                <div className="p-8 space-y-6" data-testid="contact-info">
                  <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <MapPin className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Address</p>
                        <p className="text-muted-foreground">54 St. Paul Crescent</p>
                        <p className="text-muted-foreground">Barrie, ON L9J 0M2</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <Mail className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Email</p>
                        <a href="mailto:support@truenorthmuslimcommunity.ca" className="text-primary hover:underline block">
                          support@truenorthmuslimcommunity.ca
                        </a>
                        <a href="mailto:truenorthmuslim@gmail.com" className="text-primary hover:underline block">
                          truenorthmuslim@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Phone</p>
                        <a href="tel:249-444-5227" className="text-primary hover:underline">
                          249-444-5227
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </LaserBorder>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <h4 className="font-semibold font-['Outfit'] text-foreground mb-4">Follow Us</h4>
                <div className="space-y-3">
                  <a 
                    href="https://instagram.com/true.north.muslim"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl bg-accent/50 hover:bg-primary/10 transition-colors"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">@true.north.muslim</span>
                  </a>
                  <a 
                    href="https://facebook.com/TrueNorthMuslimCommunity"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl bg-accent/50 hover:bg-primary/10 transition-colors"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">True North Muslim Community</span>
                  </a>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-green-500/10 border border-green-500/20">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-green-500/20">
                    <MessageCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground mb-2">Join Our WhatsApp</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Get instant updates and connect with the community.
                    </p>
                    <a 
                      href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp"
                    >
                      <Button className="rounded-full bg-green-500 hover:bg-green-600 text-white">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Join Now
                      </Button>
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <h4 className="font-semibold font-['Outfit'] text-foreground mb-4">Office Hours</h4>
                <p className="text-muted-foreground text-sm">
                  We are a volunteer-run organization. Please allow 1-3 business days for email responses.
                  For urgent matters, reach out via WhatsApp.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Location"
            title="Find Us"
            description="Join us for Friday prayers or community events."
          />
          
          <div className="rounded-2xl overflow-hidden border border-border">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2851.123456789!2d-79.71234567890123!3d44.40123456789012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDI0JzA0LjQiTiA3NcKwNDInNDQuNCJX!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="True North Muslim Community Location"
              className="grayscale hover:grayscale-0 transition-all duration-500"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
